'use client';

import { Card } from '@/components/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { ScatterPlot } from '../common/scatter-plot';
import type { CoefficientOfDeterminationResult as BaseResult } from '../../../lib/stats/coefficient-of-determination';
import jStat from 'jstat';

// Re-export the result type for the calculator config
export type CoefficientOfDeterminationResult = BaseResult;

interface CoefficientOfDeterminationCalculatorProps {
  visualizationData: CoefficientOfDeterminationResult | null;
}

export function CoefficientOfDeterminationCalculator({ visualizationData: result }: CoefficientOfDeterminationCalculatorProps) {
  if (!result) {
    return null;
  }

  const { rSquared, rSquaredAdjusted, sampleSize, data1, data2, xLabel, yLabel } = result;

  // Filter out any undefined values and create scatter data points
  const validPairs = data1
    .map((x, i) => [x, data2[i]])
    .filter((pair): pair is [number, number] => 
      pair[0] !== undefined && pair[1] !== undefined && !isNaN(pair[0]) && !isNaN(pair[1])
    );

  // Calculate regression line
  const xMin = Math.min(...validPairs.map(p => p[0]));
  const xMax = Math.max(...validPairs.map(p => p[0]));
  
  // Calculate regression line points using correlation coefficient
  const correlationCoefficient = Math.sqrt(rSquared) * (rSquared >= 0 ? 1 : -1);
  const slope = correlationCoefficient * (Math.sqrt(jStat.variance(data2)) / Math.sqrt(jStat.variance(data1)));
  const intercept = jStat.mean(data2) - slope * jStat.mean(data1);
  const regressionLine: [number, number][] = [
    [xMin, slope * xMin + intercept],
    [xMax, slope * xMax + intercept]
  ];

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Coefficient of Determination Analysis</h3>
          <div className="space-y-4">
            <div>
              <p className="font-medium">Coefficient of Determination (R²):</p>
              <p>{rSquared.toFixed(4)}</p>
              <p className="text-sm text-gray-600 mt-1">
                {rSquared < 0.3 ? 'Weak fit' : rSquared < 0.7 ? 'Moderate fit' : 'Strong fit'}
              </p>
            </div>
            <div>
              <p className="font-medium">Adjusted R²:</p>
              <p>{rSquaredAdjusted.toFixed(4)}</p>
              <p className="text-sm text-gray-600 mt-1">
                Accounts for the number of predictors in the model
              </p>
            </div>
            <div>
              <p className="font-medium">Sample size:</p>
              <p>{sampleSize}</p>
            </div>
            <div>
              <p className="font-medium">Interpretation:</p>
              <p className="text-sm text-gray-600">
                {(rSquared * 100).toFixed(2)}% of the variance in {yLabel} can be explained by {xLabel}.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Scatter Plot with Regression Line</h3>
          <div className="h-[400px]">
            <ScatterPlot
              data={validPairs}
              xLabel={xLabel}
              yLabel={yLabel}
              trendLine={{
                data: regressionLine,
                color: '#4d79ff'
              }}
            />
          </div>
        </Card>
      </div>
    </ScrollableContainer>
  );
} 